import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loading } from '@/ui/components/atoms/Loading';
import { ControlsContainer } from '@/ui/components/molecules/ControlsContainer';
import { ExpertiseList } from '@/ui/components/molecules/ExpertiseList';
import { useDossierEnAttente } from '../../hooks/useDossierEnAttente';
import type { DossierEnAttenteViewProps } from '../../types/dossier-en-attente.types';
import './DossierEnAttenteView.styles.css';

export const DossierEnAttenteView: React.FC<DossierEnAttenteViewProps> = ({ 
  navigationParams 
}) => {
  const navigate = useNavigate();
  const {
    expertises,
    expertName,
    isLoading,
    error,
    filters,
    updateFilter,
  } = useDossierEnAttente(navigationParams?.filter);

  // Update filter when navigation params change
  useEffect(() => {
    if (navigationParams?.filter === 'urgent') {
      updateFilter('activeFilter', 'urgent');
    } else if (navigationParams?.filter === undefined && filters.activeFilter === 'urgent') {
      updateFilter('activeFilter', 'all');
    }
  }, [navigationParams]);

  const handleExpertiseClick = (expertiseId: number | string) => {
    navigate(`/expert/affaire/${expertiseId}`, {
      state: { from: '/expert/dossier-en-attente' }
    });
  };

  if (isLoading) {
    return (
      <div className="dossier-en-attente-container">
        <Loading 
          size="large" 
          text="Chargement des dossiers en attente..."
          fullScreen={false}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dossier-en-attente-container">
        <div className="error-message">
          {error instanceof Error ? error.message : 'Une erreur est survenue'}
        </div>
      </div>
    );
  }

  // Transform expertises data for ExpertiseList to match ExpertiseCardProps
  const expertiseListData = expertises.map(expertise => {
    const tags = [];
    
    // Check if urgent based on date
    const now = new Date();
    const appointmentDate = expertise.date_du_rv ? new Date(expertise.date_du_rv) : null;
    const isUrgent = appointmentDate && 
      Math.ceil((appointmentDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) <= 3;
    
    if (isUrgent && appointmentDate) {
      const daysDiff = Math.ceil((appointmentDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      tags.push(`${daysDiff} jour${daysDiff !== 1 ? 's' : ''} restant${daysDiff !== 1 ? 's' : ''}`);
    }

    // Add status-based tags
    const statusLower = expertise.status?.toLowerCase() || '';
    if (statusLower.includes('attente paiement')) {
      tags.push('Attente paiement');
    } else if (statusLower.includes('attente doc')) {
      tags.push('Attente documents');
    } else if (statusLower.includes('attente confirmation')) {
      tags.push('Attente confirmation');
    } else if (statusLower.includes('bureau à valoriser')) {
      tags.push('À valoriser');
    } else if (statusLower.includes('bureau à achever')) {
      tags.push('Bureau à achever');
    } else if (statusLower.includes('fch à achever')) {
      tags.push('FCH à achever');
    }

    // Add payment status tag
    if (expertise.perception) {
      if (expertise.perception === 'PAYE BANQUE' || 
          expertise.perception?.toLowerCase().includes('preuve virement')) {
        tags.push('Payé');
      } else if (expertise.perception?.toLowerCase().includes('facturer')) {
        tags.push('Facturé');
      } else if (expertise.perception === 'En attente') {
        tags.push('Attente paiement');
      }
    }
    
    // Add invoice status if relevant
    if (expertise.facture_emise === true || expertise.facture_emise === 'oui' || expertise.facture_emise === 'emise') {
      tags.push('Facture émise');
    }

    // Build full client name with contact info for display
    let clientDisplay = expertise.client?.name || 'Client non renseigné';
    if (expertise.client?.phone) {
      clientDisplay += ` - ${expertise.client.phone}`;
    }
    if (expertise.client?.email) {
      clientDisplay += ` - ${expertise.client.email}`;
    }

    // Get progress percentage based on status
    const getProgressFromStatus = (status: string) => {
      const statusLower = status?.toLowerCase() || '';
      if (statusLower.includes('expert à achever')) return 62.5;
      if (statusLower.includes('bureau à valoriser')) return 62.5;
      if (statusLower.includes('bureau à achever')) return 62.5;
      if (statusLower.includes('fch à achever')) return 62.5;
      if (statusLower.includes('attente doc') || statusLower.includes('attente info')) return 75;
      if (statusLower.includes('attente paiement')) return 75;
      if (statusLower.includes('attente confirmation')) return 87.5;
      return 50; // Default for status 50-100
    };

    // Map to ExpertiseCardProps interface
    return {
      // Core data (required)
      id: String(expertise.id),
      reference: String(expertise.id),
      title: expertise.type || 'Expertise immobilière',
      status: 'en_cours' as const, // Map to valid ExpertiseStatus
      statusDisplay: expertise.status || 'status not found', // Use actual backend status

      // Display variant
      variant: 'standard' as const,
      
      // Common fields
      clientName: clientDisplay,
      expertName: expertise.expert || expertName || 'Non assigné',
      createdAt: expertise.date || undefined,
      updatedAt: expertise.date_du_rv || undefined,
      dueDate: expertise.date_du_rv || undefined,
      
      // Address information
      address: expertise.address ? {
        street: expertise.address.join(', '),
        city: undefined,
        postalCode: undefined,
        country: 'France'
      } : undefined,
      
      // Financial information
      amount: typeof expertise.price === 'string' ? parseFloat(expertise.price) : expertise.price,
      currency: 'EUR',
      paid: expertise.perception === 'PAYE BANQUE' || 
            expertise.perception?.toLowerCase().includes('preuve virement'),
      perception: expertise.perception,
      invoiceStatus: expertise.affaire_facturee === true || expertise.affaire_facturee === 'oui' ? 'emise' : undefined,
      invoiceIssued: expertise.facture_emise === true || expertise.facture_emise === 'oui' || expertise.facture_emise === 'emise',
      
      // Progress information
      progress: getProgressFromStatus(expertise.status || ''),
      stage: expertise.status || 'En attente',
      stageId: 0,
      priority: isUrgent ? 'urgent' as const : 'medium' as const,
      
      // Tags and categories
      tags,
      category: 'dossier-en-attente',
      type: expertise.type || 'Expertise immobilière',
      
      // Description and notes  
      description: expertise.heure_rv ? `RV à ${expertise.heure_rv.substring(0, 5)}` : undefined,
      notes: undefined,
      
      // Statistics
      documentsCount: undefined,
      messagesCount: undefined,
      tasksCompleted: undefined,
      tasksPending: undefined,
      
      // UI customization
      className: isUrgent ? 'urgent-expertise' : undefined,
      showActions: true,
      selectable: false,
      selected: false,
      disabled: false,
      loading: false
    };
  });

  // Calculate stats for display
  const displayStats = {
    total: expertises.length,
    urgent: expertises.filter(e => {
      const appointmentDate = e.date_du_rv ? new Date(e.date_du_rv) : null;
      return appointmentDate && 
        Math.ceil((appointmentDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) <= 3;
    }).length,
    attente_paiement: expertises.filter(e => 
      e.status?.toLowerCase().includes('attente paiement') || 
      e.status?.toLowerCase().includes('rappel paiement')
    ).length,
    attente_doc: expertises.filter(e => 
      e.status?.toLowerCase().includes('attente doc') || 
      e.status?.toLowerCase().includes('attente info')
    ).length
  };

  return (
    <div className="dossier-en-attente-page">
      {/* Header with title and stats - matching a-traiter style */}
      <div className="dossier-en-attente-page__header">
        <div className="dossier-en-attente-page__header-content">
          <h1 className="dossier-en-attente-page__title">
            Dossiers en attente
          </h1>
          <p className="dossier-en-attente-page__subtitle">
            Gérez vos dossiers en cours et en attente de traitement
          </p>
        </div>
        
        <div className="dossier-en-attente-page__actions">
          {displayStats && (
            <>
              <span className="dossier-en-attente-stat">
                Total: <strong>{displayStats.total}</strong>
              </span>
              {displayStats.urgent > 0 && (
                <span className="dossier-en-attente-stat">
                  Urgents: <strong>{displayStats.urgent}</strong>
                </span>
              )}
              {displayStats.attente_paiement > 0 && (
                <span className="dossier-en-attente-stat">
                  Attente paiement: <strong>{displayStats.attente_paiement}</strong>
                </span>
              )}
              {displayStats.attente_doc > 0 && (
                <span className="dossier-en-attente-stat">
                  Attente docs: <strong>{displayStats.attente_doc}</strong>
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Search and controls bar */}
      <div className="dossier-en-attente-page__controls">
        <ControlsContainer
          searchQuery={filters.searchQuery}
          onSearchChange={(value) => updateFilter('searchQuery', value)}
          sortBy={filters.sortBy}
          onSortChange={(value) => updateFilter('sortBy', value as 'date' | 'type' | 'status' | 'price')}
          sortOrder={filters.sortOrder}
          onSortOrderChange={(value) => updateFilter('sortOrder', value)}
          sortOptions={[
            { value: 'date', label: 'Date demande' },
            { value: 'status', label: 'Statut' },
            { value: 'type', label: 'Type' },
            { value: 'price', label: 'Prix' }
          ]}
          activeFilter={filters.activeFilter}
          onFilterChange={(value) => updateFilter('activeFilter', value as 'all' | 'urgent' | 'overdue' | 'payment-pending')}
          filterOptions={[
            { key: 'all', label: 'Tous' },
            { key: 'urgent', label: 'Urgent' },
            { key: 'payment-pending', label: 'Attente paiement' },
            { key: 'overdue', label: 'En retard' }
          ]}
          showFilters={true}
          resultsCount={expertises.length}
          searchPlaceholder="Rechercher par adresse, client, numéro, statut..."
          className="dossier-en-attente-controls"
        />
      </div>

      {/* Expertise list */}
      <div className="dossier-en-attente-page__content">
        <ExpertiseList
          expertises={expertiseListData}
          onExpertiseClick={(expertise) => handleExpertiseClick(expertise.id)}
          showFacadeImage={true}
          showProgress={true}
          showActions={true}
          emptyStateTitle="Aucun dossier en attente"
          emptyStateMessage={
            filters.searchQuery || filters.activeFilter !== 'all'
              ? 'Aucun dossier ne correspond aux critères de recherche'
              : 'Aucun dossier en attente pour le moment'
          }
          isSearchActive={!!filters.searchQuery || filters.activeFilter !== 'all'}
          className="dossier-en-attente-list"
        />
      </div>
    </div>
  );
};